import React, { createContext, useContext, useState, useEffect } from 'react';
import { navigate } from 'gatsby';
import {
  signIn as AmplifySignIn,
  getCurrentUser,
  confirmSignIn as AmplifyConfirmSignIn,
  signOut as AmplifySignOut,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

const defaultAuthContext = {
  user: null,
  signIn: () => {},
  signUp: () => {},
  signOut: () => {},
  confirmSignIn: () => {},
  error: '',
  role: '', // Default role
};

const AuthContext = createContext(defaultAuthContext);

const ALLOWED_DOMAIN = 'kpoint.com';

const clearAmplifyStorage = () => {
  const isAmplifyKey = key =>
    key.startsWith('CognitoIdentityServiceProvider') ||
    key.startsWith('amplify-') ||
    key.includes('oauth');
  Object.keys(localStorage).filter(isAmplifyKey).forEach(k => localStorage.removeItem(k));
  Object.keys(sessionStorage).filter(isAmplifyKey).forEach(k => sessionStorage.removeItem(k));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState({ state: 'NOT_FETCHED_YET' });
  const [error, setError] = useState('');
  const [role, setRole] = useState(''); // Default role

  useEffect(() => {
    // Read any stored auth error synchronously before checkUser runs.
    // Done here (not in lazy useState) to avoid SSR/hydration mismatch —
    // sessionStorage doesn't exist on the server so lazy init would produce
    // different HTML between SSR and client, causing React hydration error #418.
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('authError');
      if (stored) {
        sessionStorage.removeItem('authError');
        setError(stored);
      }

      // If signInWithGoogle stored this flag, we routed through Cognito logout
      // and are now back at origin/. Fire signInWithRedirect here (inside React,
      // while user is still NOT_FETCHED_YET) so PrivateRoute returns null and
      // never redirects to /login/ before the OAuth redirect fires.
      const pending = sessionStorage.getItem('pendingGoogleSignIn');
      if (pending === 'true') {
        sessionStorage.removeItem('pendingGoogleSignIn');
        signInWithRedirect({ provider: 'Google' });
        return; // skip checkUser — page is about to navigate away
      }
    }

    checkUser();

    const unsubscribe = Hub.listen('auth', (capsule) => {
      const event = capsule?.payload?.event ?? capsule?.event;
      if (!event) return;
      if (event === 'signInWithRedirect') {
        checkUser();
      } else if (event === 'signInWithRedirect_failure') {
        setError('Google sign-in failed. Please try again.');
      }
    });

    return () => unsubscribe();
  }, []);

  const checkUser = async () => {
    try {
      const userData = await getCurrentUser();
      const userDetails = await fetchUserAttributes();

      // Restrict Google sign-in to @kpoint.com accounts only
      const identitiesRaw = userDetails['identities'];
      if (identitiesRaw) {
        try {
          const identities = JSON.parse(identitiesRaw);
          const isGoogleSignIn = identities.some(id => id.providerName === 'Google');
          if (isGoogleSignIn) {
            const email = userDetails['email'] || '';
            if (!email.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`)) {
              try { await AmplifySignOut(); } catch (_) {}
              clearAmplifyStorage();
              if (typeof window !== 'undefined') {
                sessionStorage.setItem('authError', `Access restricted to @${ALLOWED_DOMAIN} accounts only.`);
                window.location.href = '/login/';
                return; // stop here — setUser(null) must NOT run, it would
                        // trigger PrivateRoute to client-navigate to /login/
                        // before the full page reload, causing a double flash
              }
              setUser(null);
              return;
            }
          }
        } catch (_) {}
      }

      const userRole = userDetails['custom:Role'] || 'viewer';
      setRole(userRole);
      setUser({ ...userData, isSignedIn: true });
    } catch (error) {
      setUser(null);
    }
  };

  const signIn = async (username, password) => {
    try {
      // Clear any stale session before signing in
      try { await AmplifySignOut(); } catch (_) {}
      const res = await AmplifySignIn({ username, password });

      if (
        res.nextStep?.signInStep ===
        'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        setUser({ ...res, isSignedIn: false }); // Not fully signed in yet, needs password confirmation
      } else {
        // Retrieve the intended URL after login
        const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin');

        // Force update user details including roles after login
        await checkUser(); // Ensure this fetches the latest role-based data

        const navigateUrl = redirectAfterLogin || '/';

        // Remove the redirectAfterLogin from session storage
        sessionStorage.removeItem('redirectAfterLogin');

        // Use window.location.href to navigate and refresh the page
        window.location.href = navigateUrl;
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const signUp = async (username, password, email) => {
    try {
      await signUp({
        username,
        password,
        attributes: {
          email,
        },
      });
      navigate('/confirmation');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const signInWithGoogle = () => {
    if (typeof window === 'undefined') return;
    // Clear all local Amplify/OAuth/PKCE state
    clearAmplifyStorage();
    // Mark intent so gatsby-browser.js can auto-fire signInWithRedirect
    // after Cognito's logout endpoint clears the server-side session cookie
    sessionStorage.setItem('pendingGoogleSignIn', 'true');
    // Redirect through Cognito's logout endpoint — this clears the hosted UI
    // session cookie so Google always shows the account chooser next time
    const redirectUri = encodeURIComponent(`${window.location.origin}/`);
    window.location.href =
      `https://engage-auth.auth.ap-south-1.amazoncognito.com/logout` +
      `?client_id=605rpm3gpo95j288tnqvhbsbd` +
      `&logout_uri=${redirectUri}`;
  };

  const signOut = () => {
    // Clear tokens synchronously first — this prevents AmplifySignOut from
    // doing its own redirect, and prevents React from re-rendering with
    // user=null which would cause PrivateRoute to flash the login page
    clearAmplifyStorage();
    if (typeof window !== 'undefined') {
      window.location.href = '/logout';
    }
  };

  const confirmSignIn = async newPassword => {
    try {
      const res = await AmplifyConfirmSignIn({
        challengeResponse: newPassword,
        userObject: user,
      });
      // After confirming sign in, update user state and refresh to get full user details
      await checkUser();
      window.location.href = '/';
    } catch (error) {
      setError(error.message);
    }
  };

  const requestPasswordReset = async (username) => {
    try {
      const output = await resetPassword({ username });
      return { success: true, output };
    } catch (error) {
      return { success: false, error };
    }
  };

  const confirmPasswordReset = async (username, code, newPassword) => {
    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword
      });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signUp,
        signOut,
        signInWithGoogle,
        confirmSignIn,
        requestPasswordReset,
        confirmPasswordReset,
        error,
        setError,
        role,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
